import React, { useState, useEffect, useCallback } from "react";
import CoachPlansSkeleton from "../../components/layout/CoachPlansSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Ticket, Loader2, ArrowLeft } from "lucide-react";
import CoachPlanCards from "../../components/CoachPlanCards";
import PlanChangeConfirm from "../../components/PlanChangeConfirm";
import { actionLabel, day, money } from "../../utils/planChange";

// The premium features a coach plan can unlock, by key (a plan no longer on sale shows them)
const COACH_FEATURE_NAMES = { coach_reporting: "Coach Reporting", my_program: "My Program" };

export default function CoachPlans() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState("");
    const [redeeming, setRedeeming] = useState(false);
    const [subscribing, setSubscribing] = useState(null);
    const [message, setMessage] = useState(null);
    const [currentTierId, setCurrentTierId] = useState(null);
    // Preview only — a code grants nothing until a plan is confirmed below.
    const [promo, setPromo] = useState(null); // {code, discountType, discountValue, tiers}
    const [membership, setMembership] = useState(null); // the coach's own plan: what a pick does
    const [trialAvailable, setTrialAvailable] = useState(false); // a first subscription gets the plan's trial
    // The confirm window: {plan, preview} for a paid plan, {plan, free: true} for the free plan
    const [change, setChange] = useState(null);
    const [changing, setChanging] = useState(false);
    const [changeError, setChangeError] = useState("");

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch the plans and the coach's own membership together so the
            // current plan is highlighted. A coach with no membership row is on
            // the free tier by default, so fall back to the free plan.
            const [plansRes, memRes] = await Promise.allSettled([
                api.get("/coach/plans"),
                api.get("/coach/my-membership"),
            ]);
            const m = memRes.status === "fulfilled" ? memRes.value.data.membership : null;
            setMembership(m?.isActive ? m : null);
            setTrialAvailable(plansRes.status === "fulfilled" && plansRes.value.data.trialAvailable === true);
            // A plan closed to new members (Open for new enrollment off) only
            // shows for the coach who's already on it.
            const plansData = (plansRes.status === "fulfilled" ? (plansRes.value.data.plans || []) : [])
                .filter((pl) => (pl.OpenForEnrollment !== false && pl.OpenForEnrollment !== 0)
                    || (m?.isActive && m?.TierID === pl.TierID));
            // The coach's own plan always shows (as Current Plan), also once it's turned off or
            // isn't a coach plan anymore: the list has only plans on sale, so it comes from the membership
            if (m?.isActive && m?.TierID && !plansData.some((pl) => pl.TierID === m.TierID)) {
                const unlocked = memRes.value.data.unlockedFeatures || [];
                plansData.push({ TierID: m.TierID, TierName: m.TierName, TierDescription: m.TierDescription, Price: m.Price,
                    BillingFrequency: m.BillingFrequency, TrialDays: m.TrialDays,
                    Features: unlocked.map((key) => COACH_FEATURE_NAMES[key]).filter(Boolean) });
                plansData.sort((a, b) => Number(a.Price) - Number(b.Price));
            }
            setPlans(plansData);
            if (m?.isActive && m?.TierID) {
                setCurrentTierId(m.TierID);
            } else {
                const freePlan = plansData.find((pl) => Number(pl.Price) === 0 && !pl.TrialDays);
                setCurrentTierId(freePlan ? freePlan.TierID : null);
            }
        } catch {
            setPlans([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const showMsg = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    const describeDiscount = (p) => {
        if (!p) return "";
        if (p.discountType === "free") return "free";
        if (p.discountType === "percent") return `${Number(p.discountValue)}% off`;
        return `$${Number(p.discountValue || 0).toFixed(2)} off`;
    };

    // The promo's effect on one plan, or null when this plan isn't covered.
    const promoFor = (tierId) => promo?.tiers?.find((t) => t.TierID === tierId) || null;
    // Paid by card: another paid plan changes that subscription
    const paysByCard = !!membership?.HasSubscription;

    // Applying a code only previews it: the plans below re-price, nothing is
    // granted and the code's redemption count is untouched until a plan is
    // confirmed.
    const handleRedeem = async () => {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) return;
        try {
            setRedeeming(true);
            const res = await api.post("/coach/code/preview", { code: trimmed });
            //setPromo(res.data);
            //const n = res.data?.tiers?.length || 0;
            // Only plans on this page count: a code for student plans changes none of them
            const n = (res.data?.tiers || []).filter((t) => plans.some((p) => p.TierID === t.TierID)).length;
            if (n === 0) {
                showMsg(`${trimmed} isn't for coach plans.`, "error");
                return;
            }
            setPromo(res.data);
            showMsg(`${trimmed} applied — ${describeDiscount(res.data)} on ${n} plan${n === 1 ? "" : "s"} below.`, "success");
        } catch (err) {
            showMsg(err.response?.data?.error || "That code isn't valid or has expired.", "error");
        } finally {
            setRedeeming(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setCode("");
    };

    const handleSubscribe = async (plan) => {
        const isFree = Number(plan.Price) === 0 && !plan.TrialDays;
        try {
            setSubscribing(plan.TierID);
            // A 'free' (full grant) code covering this plan: this confirmation is
            // what actually claims it — the code alone granted nothing.
            //if (promo && promo.discountType === "free" && promoFor(plan.TierID)) {
            // (a $0 plan doesn't need the code: the usual switch keeps a paid plan until it ends)
            if (promo && promo.discountType === "free" && promoFor(plan.TierID) && Number(plan.Price) > 0) {
                await api.post("/coach/redeem-code", { code: promo.code, tier_id: plan.TierID });
                //old showMsg("Plan activated.", "success");
                //old setTimeout(() => navigate("/coaching/membership"), 900);
                // Said on My Membership, where it stays long enough to read (it flashed for 0.9s here)
                navigate("/coaching/membership", { state: { notice: "Plan activated." } });
                return;
            }
            if (isFree) {
                //old const res = await api.post("/coach/select-free-plan", { tier_id: plan.TierID });
                //old showMsg(res.data.message || "Plan activated.", "success");
                //old setTimeout(() => navigate("/coaching/membership"), 900);
                // Asked in the window first (one way out, like Cancel), then said on My Membership
                setChangeError("");
                setChange({ plan, free: true });
                setSubscribing(null);
                return;
            }
            // A coach who pays by card changes that plan instead: the window says what's charged
            // today, or when a cheaper plan starts (never a second checkout)
            if (paysByCard) {
                try {
                    const res = await api.post("/coach/change-plan/preview", { tier_id: plan.TierID });
                    setChangeError("");
                    setChange({ plan, preview: res.data });
                    setSubscribing(null);
                    return;
                } catch (previewErr) {
                    // The last renewal didn't go through (PAST_DUE): no other paid plan until the card pays it. The note
                    // at the top of the page says so, brought into view (on a phone the card is further down) and kept
                    // there (it's longer than the page's other notes, which go after 5 seconds)
                    if (previewErr.response?.data?.code === "PAST_DUE") {
                        setMessage({ text: previewErr.response.data.error, type: "error" });
                        setSubscribing(null);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        return;
                    }
                    // Not paid by card anymore (the subscription just ended): bought at checkout below
                    if (previewErr.response?.data?.code !== "NO_SUBSCRIPTION") throw previewErr;
                }
            }
            //const res = await api.post("/coach/create-checkout-session", { tier_id: plan.TierID });
            // A percent/amount code applied above lowers this checkout
            const res = await api.post("/coach/create-checkout-session", {
                tier_id: plan.TierID,
                promo_code: promo && promo.discountType !== "free" && promoFor(plan.TierID) ? promo.code : undefined,
            });
            if (res.data.checkout_url) window.location.href = res.data.checkout_url;
            else { showMsg("Could not start checkout.", "error"); setSubscribing(null); }
        } catch (err) {
            showMsg(err.response?.data?.error || "Could not start checkout.", "error");
            setSubscribing(null);
        }
    };

    const closeChange = () => {
        if (changing) return;
        setChange(null);
        setChangeError("");
    };

    // Confirmed in the window: a paid plan changes the subscription (now, or when the paid
    // period ends); the free plan waits for the paid period like Cancel (Evan, Sep 9)
    const confirmChange = async () => {
        const { plan, preview, free } = change;
        setChanging(true);
        setChangeError("");
        try {
            if (free) {
                const res = await api.post("/coach/select-free-plan", { tier_id: plan.TierID });
                // Said like the student page (the paid plan waits for its end when it's paid by card)
                navigate("/coaching/membership", { state: { notice: res.data.membership?.PendingTierID
                    ? `You keep ${membership.TierName} until ${day(membership.EndDate)}, then move to ${plan.TierName}. You won't be charged again.`
                    : `You're on ${plan.TierName} now.` } });
                return;
            }
            const res = await api.post("/coach/change-plan", {
                tier_id: plan.TierID, proration_date: preview.prorationDate, amount_due: preview.amountDue,
            });
            navigate("/coaching/membership", { state: { notice: res.data?.scheduled
                ? `${plan.TierName} starts on ${day(res.data.effectiveOn)}. You keep ${membership.TierName} until then.`
                : `You're on ${plan.TierName} now.${Number(res.data?.amountPaid) > 0 ? ` ${money(res.data.amountPaid)} was charged to your card.` : ""}` } });
        } catch (err) {
            const errCode = err.response?.data?.code;
            if (errCode === "AMOUNT_CHANGED" || errCode === "PREVIEW_EXPIRED") {
                // Checked again: the window shows the new amount to confirm
                try {
                    const res = await api.post("/coach/change-plan/preview", { tier_id: plan.TierID });
                    setChange({ plan, preview: res.data });
                    setChangeError(`${err.response.data.error} The amount below is up to date.`);
                } catch (previewErr) {
                    setChangeError(previewErr.response?.data?.error || "Could not check the price. Try again.");
                }
            } else {
                setChangeError(err.response?.data?.error || "Could not change your plan. Try again.");
            }
        } finally {
            setChanging(false);
        }
    };

    // A card's button: Upgrade / Downgrade / Switch, or the plan already picked for the renewal
    const actionFor = (plan) => (membership?.PendingTierID === plan.TierID
        ? { label: `Starts ${day(membership.EndDate)}`, disabled: true }
        : { label: actionLabel(membership, plan, { paysByCard }) });

    const heading = isDarkMode ? "text-slate-100" : "text-slate-900";
    const sub = isDarkMode ? "text-slate-400" : "text-slate-600";
    const card = isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";

    if (loading) return <CoachPlansSkeleton isDarkMode={isDarkMode} />;

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-10">
                <button onClick={() => navigate("/coaching/membership")} className={`inline-flex items-center gap-1.5 text-sm font-semibold mb-6 ${sub} hover:underline`}>
                    <ArrowLeft size={16} /> Back to My Membership
                </button>

                <header className="text-center mb-8">
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-1 ${heading}`}>Choose a plan</h1>
                    <p className={sub}>Upgrade your coach membership to unlock premium tools.</p>
                </header>

                {message && (
                    <div role="alert" className={`mb-6 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>{message.text}</div>
                )}

                {/* Referral code */}
                <div className={`rounded-2xl border shadow-sm p-5 mb-6 ${card}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-slate-700 text-primary-400" : "bg-primary-50 text-primary-600"}`}>
                                <Ticket size={20} />
                            </div>
                            <div>
                                {/* old <h2 className={`font-bold ${heading}`}>Have a referral code?</h2> */}
                                <h2 className={`font-bold ${heading}`}>Have a promo code?</h2>
                                <p className={`text-sm ${sub}`}>Enter it to unlock or discount your membership.</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => { if (e.key === "Enter") handleRedeem(); }}
                                disabled={!!promo}
                                placeholder="Enter code"
                                //old className={`flex-1 sm:w-48 p-2.5 border rounded-lg font-mono uppercase ${isDarkMode ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500" : "border-slate-300"}`}
                                // min-w-0: on a phone the box shrinks to leave room for Apply (it pushed the button off the screen)
                                className={`flex-1 min-w-0 sm:w-48 p-2.5 border rounded-lg font-mono uppercase ${isDarkMode ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500" : "border-slate-300"}`}
                            />
                            {promo ? (
                                <button
                                    onClick={clearPromo}
                                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold border transition-colors ${isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                                >
                                    Remove
                                </button>
                            ) : (
                                <button
                                    onClick={handleRedeem}
                                    disabled={redeeming || !code.trim()}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                >
                                    {redeeming ? <Loader2 size={18} className="animate-spin" /> : <Ticket size={18} />}
                                    {redeeming ? "Applying..." : "Apply"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Plans */}
                {/* old <CoachPlanCards plans={plans} onChoose={handleSubscribe} busyTierId={subscribing} currentTierId={currentTierId} promo={promo} /> */}
                <CoachPlanCards plans={plans} onChoose={handleSubscribe} busyTierId={subscribing} currentTierId={currentTierId} promo={promo}
                    trialAvailable={trialAvailable} actionFor={actionFor} />
            </div>

            {/* Before a plan changes: what's charged today, or when it starts */}
            {change && (
                <PlanChangeConfirm
                    change={change}
                    membership={membership}
                    paysByCard={paysByCard}
                    // Without a card behind the plan, the free plan starts now (nothing to run out)
                    freeNow={!paysByCard}
                    // A code entered here is for a new subscription, not a change to the one paid by card
                    // (a discount that subscription already has stays on it)
                    promoNote={!change.free && promo && promo.discountType !== "free" && promoFor(change.plan.TierID)
                        ? `${promo.code} is for new subscriptions, so it isn't used for this change.` : null}
                    error={changeError}
                    busy={changing}
                    onClose={closeChange}
                    onConfirm={confirmChange}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
}
